/**
 * Minimal MD5 (RFC 1321) for optional chunk verification against manifest md5s.
 * WebCrypto has no MD5, so this is a small pure implementation (≈ 200 MB/s in V8; verification is opt-in).
 */

const S = new Uint8Array([
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
]);
const K = new Uint32Array(64);
for (let i = 0; i < 64; i++) K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0;

/** md5 of the bytes, lower-case hex. */
export function md5Hex(data: ArrayBuffer | ArrayBufferView): string {
  const src = data instanceof ArrayBuffer
    ? new Uint8Array(data)
    : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  const len = src.length;
  const padded = ((len + 8) >>> 6) + 1;            // 64-byte blocks
  const buf = new Uint8Array(padded * 64);
  buf.set(src);
  buf[len] = 0x80;
  const dv = new DataView(buf.buffer);
  const bitsLo = (len * 8) >>> 0;
  const bitsHi = Math.floor(len / 0x20000000) >>> 0;
  dv.setUint32(buf.length - 8, bitsLo, true);
  dv.setUint32(buf.length - 4, bitsHi, true);

  let a0 = 0x67452301, b0 = 0xefcdab89, c0 = 0x98badcfe, d0 = 0x10325476;
  const M = new Uint32Array(16);
  for (let blk = 0; blk < padded; blk++) {
    for (let j = 0; j < 16; j++) M[j] = dv.getUint32(blk * 64 + j * 4, true);
    let A = a0, B = b0, C = c0, D = d0;
    for (let i = 0; i < 64; i++) {
      let F: number, g: number;
      if (i < 16) { F = (B & C) | (~B & D); g = i; }
      else if (i < 32) { F = (D & B) | (~D & C); g = (5 * i + 1) & 15; }
      else if (i < 48) { F = B ^ C ^ D; g = (3 * i + 5) & 15; }
      else { F = C ^ (B | ~D); g = (7 * i) & 15; }
      F = (F + A + (K[i] ?? 0) + (M[g] ?? 0)) >>> 0;
      A = D; D = C; C = B;
      const s = S[i] ?? 0;
      B = (B + ((F << s) | (F >>> (32 - s)))) >>> 0;
    }
    a0 = (a0 + A) >>> 0; b0 = (b0 + B) >>> 0; c0 = (c0 + C) >>> 0; d0 = (d0 + D) >>> 0;
  }
  let hex = '';
  for (const w of [a0, b0, c0, d0]) {
    for (let k = 0; k < 4; k++) hex += ((w >>> (8 * k)) & 255).toString(16).padStart(2, '0');
  }
  return hex;
}
